import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useDarkMode } from "../../hooks/useDarkMode";
import { Helmet } from "react-helmet";

const emptyAccount = {
  firstname: "",
  lastname: "",
  email: "",
  phone: "",
  profileImage: "",
};

const ProfilePage = () => {
  const { isDarkMode } = useDarkMode();
  const [account, setAccount] = useState(emptyAccount);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);

      // Check all possible storage locations
      const account_data = localStorage.getItem("account");
      const user_data = localStorage.getItem("user");
      const userName = localStorage.getItem("userName");
      const userEmail = localStorage.getItem("userEmail");
      const token = localStorage.getItem("token");

      let firstname = "";
      let lastname = "";
      let email = "";
      let profileImage = "";

      // Try parsing account or user object first
      if (
        account_data &&
        account_data !== "undefined" &&
        account_data !== "null"
      ) {
        try {
          const parsed = JSON.parse(account_data);
          firstname = parsed.firstname || parsed.first_name || "";
          lastname = parsed.lastname || parsed.last_name || "";
          email = parsed.email || "";
          profileImage = parsed.profileImage || "";
        } catch (e) {
          console.error("Error parsing account:", e);
        }
      } else if (
        user_data &&
        user_data !== "undefined" &&
        user_data !== "null"
      ) {
        try {
          const parsed = JSON.parse(user_data);
          firstname = parsed.firstname || parsed.first_name || "";
          lastname = parsed.lastname || parsed.last_name || "";
          email = parsed.email || "";
          profileImage = parsed.profileImage || "";
        } catch (e) {
          console.error("Error parsing user:", e);
        }
      }

      // If still no data, try individual items
      if (!firstname && !email && userName) {
        const parts = userName.split(" ");
        firstname = parts[0] || "";
        lastname = parts.slice(1).join(" ") || "";
        email = userEmail || "";
      }

      // If STILL no data but token exists, try to reconstruct from token or email
      if (!email && token) {
        // Try to extract email from token (JWT decode - basic attempt)
        try {
          const parts = token.split(".");
          if (parts.length === 3) {
            const decoded = JSON.parse(atob(parts[1]));
            if (decoded.email) {
              email = decoded.email;
              const name = decoded.name || decoded.email.split("@")[0];
              const [first, ...rest] = name.split(" ");
              firstname = first || "";
              lastname = rest.join(" ") || "";

              // Save this reconstructed data
              const accountData = {
                firstname,
                lastname,
                email,
                profileImage: "",
              };
              localStorage.setItem("account", JSON.stringify(accountData));
              localStorage.setItem(
                "userName",
                `${firstname} ${lastname}`.trim(),
              );
              localStorage.setItem("userEmail", email);
            }
          }
        } catch (err) {}
      }

      setAccount({
        firstname: firstname || "",
        lastname: lastname || "",
        email: email || "",
        phone: "",
        profileImage: profileImage || "",
      });

      setLoading(false);
    };

    loadProfile();

    const handleProfileUpdate = (event) => {
      setAccount((prev) => ({ ...prev, ...event.detail }));
    };

    window.addEventListener("profileUpdated", handleProfileUpdate);
    return () =>
      window.removeEventListener("profileUpdated", handleProfileUpdate);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("account");
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("authToken");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("userName");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("credits");
    sessionStorage.clear();
    navigate("/signin", { replace: true });
  };

  return (
    <>
    

<Helmet>

  {/* Title */}
  <title>User Profile | Seo Up Rank Dashboard</title>

  {/* Description */}
  <meta
    name="description"
    content="Manage your Seo Up Rank profile, update account details and access your personal SEO dashboard settings."
  />

  {/* Important for private pages */}
  <meta name="robots" content="noindex, nofollow" />

  {/* Canonical */}
  <link rel="canonical" href="https://novarsis.ai/profile" />

  {/* Open Graph */}
  <meta property="og:title" content="Seo Up Rank Profile" />
  <meta
    property="og:description"
    content="Access and manage your Seo Up Rank profile and account settings."
  />
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://novarsis.ai/profile" />
  <meta property="og:site_name" content="Seo Up Rank" />

  {/* Twitter */}
  <meta name="twitter:card" content="summary" />
  <meta name="twitter:title" content="Seo Up Rank Profile" />
  <meta
    name="twitter:description"
    content="Manage your Seo Up Rank account profile and settings."
  />

  {/* Schema */}
  <script type="application/ld+json">
    {`
    {
      "@context":"https://schema.org",
      "@type":"WebPage",
      "name":"User Profile - Seo Up Rank",
      "url":"https://novarsis.ai/profile",
      "description":"User profile page for managing account settings in Seo Up Rank."
    }
    `}
  </script>

</Helmet>
  
    <div
      className={`${isDarkMode ? "bg-gray-950 text-white" : "bg-gray-50 text-gray-900"} min-h-screen py-10 px-4`}
    >
      <div className="max-w-4xl mx-auto">
        <header className="mb-6">
          <h1
            className={`text-2xl md:text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            Profile
          </h1>
          <p
            className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
          >
            Manage your account details and settings
          </p>
        </header>

        <div
          className={`grid grid-cols-1 lg:grid-cols-3 items-center justify-center gap-6`}
        >
          {/* Left: Avatar & quick actions */}
          <div
            className={`col-span-1 rounded-xl shadow p-6 ${isDarkMode ? "bg-gray-900" : "bg-white"}`}
          >
            <div className="flex flex-col items-center">
              {account.profileImage ? (
                <img
                  src={account.profileImage}
                  alt="avatar"
                  className="h-30 w-30 lg:w-28 lg:h-28 rounded-full border-4 border-[#008300] object-cover shadow-md "
                />
              ) : (
                <div className="h-30 w-30 lg:w-28 lg:h-28 rounded-full border-4 border-[#008300] bg-gradient-to-br from-[#008300] to-[#006600] flex items-center justify-center shadow-md">
                  <span className="text-5xl lg:text-4xl font-bold text-white">
                    {(account.firstname?.charAt(0) || "U").toUpperCase()}
                  </span>
                </div>
              )}
              <h2
                className={`mt-4 text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {account.firstname} {account.lastname}
              </h2>
              <p
                className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                {account.email}
              </p>

              <div className="w-full mt-6 space-y-3">
                <Link
                  to="/dashboard/edit-profile"
                  className="block w-full text-center px-4 py-2 rounded-lg bg-[#008300] hover:bg-[#006600] text-white font-medium"
                >
                  Edit Profile
                </Link>
                <button
                  onClick={handleLogout}
                  className="block w-full text-center px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium " 
                >
                  Logout
                </button>
              </div>
            </div>
          </div>

          {/* Right: Details and activity */}
          <div className={`col-span-2 space-y-6`}>
            <div
              className={`rounded-xl shadow p-6 ${isDarkMode ? "bg-gray-900" : "bg-white"}`}
            >
              <h3 className="text-lg font-semibold mb-4">Account Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">First Name</p>
                  <p
                    className={`mt-1 font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {account.firstname || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Last Name</p>
                  <p
                    className={`mt-1 font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {account.lastname || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p
                    className={`mt-1 font-medium break-all ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {account.email || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p
                    className={`mt-1 font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {account.phone || "-"}
                  </p>
                </div>
              </div>
            </div>

            <div
              className={`rounded-xl shadow p-6 ${isDarkMode ? "bg-gray-900" : "bg-white"}`}
            >
              <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
              <p
                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                No recent activity to show.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>  </>
  );
};

export default ProfilePage;
